import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Chip,
  TextField,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Comment,
  Share,
  Send,
  DirectionsWalk,
  EmojiEvents,
  Timeline
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';

const ActivityFeed = ({ activities, onLike, onComment, onShare, loading }) => {
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [newComments, setNewComments] = useState({});

  const toggleComments = (activityId) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  const handleCommentSubmit = async (activityId) => {
    const commentText = newComments[activityId];
    if (commentText && commentText.trim() && onComment) {
      await onComment(activityId, commentText.trim());
      setNewComments(prev => ({ ...prev, [activityId]: '' }));
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'steps':
        return <DirectionsWalk />;
      case 'challenge':
        return <EmojiEvents />;
      case 'achievement':
        return <Timeline />;
      default:
        return <DirectionsWalk />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'steps':
        return '#4CAF50';
      case 'challenge':
        return '#FF9800';
      case 'achievement':
        return '#9C27B0';
      default:
        return '#2196F3';
    }
  };

  const formatActivityData = (activity) => {
    const { type, data } = activity;
    
    switch (type) {
      case 'steps':
        if (data?.steps && data?.goal) {
          const percentage = Math.round((data.steps / data.goal) * 100);
          return `${percentage}% of daily goal (${data.steps.toLocaleString()} / ${data.goal.toLocaleString()})`;
        }
        return data?.steps ? `${data.steps.toLocaleString()} steps` : '';
        
      case 'challenge':
        if (data?.rank && data?.totalSteps) {
          return `Rank #${data.rank} with ${data.totalSteps.toLocaleString()} steps`;
        }
        return data?.challengeName || '';
        
      case 'achievement':
        return data?.streak ? `${data.streak} day streak` : data?.achievement || '';
        
      default:
        return '';
    }
  };

  if (!activities || activities.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No activity to show
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Follow friends to see their activities here
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {activities.map((activity) => {
        const isCommentsExpanded = expandedComments.has(activity.id);
        
        return (
          <Card key={activity.id}>
            <CardContent>
              {/* Activity Header */}
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar 
                  src={activity.user?.avatar} 
                  sx={{ mr: 2, bgcolor: 'primary.main' }}
                >
                  {activity.user?.name?.charAt(0)}
                </Avatar>
                <Box flexGrow={1}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {activity.user?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(parseISO(activity.timestamp), { addSuffix: true })}
                  </Typography>
                </Box>
                <Chip 
                  icon={getActivityIcon(activity.type)}
                  label={activity.type}
                  size="small"
                  sx={{ 
                    bgcolor: `${getActivityColor(activity.type)}20`,
                    color: getActivityColor(activity.type)
                  }}
                />
              </Box>

              {/* Activity Content */}
              <Typography variant="body1" paragraph>
                {activity.description}
              </Typography>

              {/* Activity Data */}
              {formatActivityData(activity) && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {formatActivityData(activity)}
                  </Typography>
                </Box>
              )}

              {/* Activity Actions */}
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton 
                    size="small" 
                    onClick={() => onLike && onLike(activity.id, !activity.isLiked)}
                    color={activity.isLiked ? 'error' : 'default'}
                  >
                    {activity.isLiked ? <Favorite /> : <FavoriteBorder />}
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {activity.likes || 0}
                  </Typography>

                  <IconButton 
                    size="small" 
                    onClick={() => toggleComments(activity.id)}
                  >
                    <Comment />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {activity.comments || 0}
                  </Typography>

                  {onShare && (
                    <IconButton size="small" onClick={() => onShare(activity.id)}>
                      <Share />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Comments Section */}
              <Collapse in={isCommentsExpanded}>
                <Box mt={2}>
                  <Divider sx={{ mb: 2 }} />
                  
                  {/* Comment Input */}
                  <Box display="flex" gap={1} mb={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Write a comment..."
                      value={newComments[activity.id] || ''}
                      onChange={(e) => setNewComments(prev => ({
                        ...prev,
                        [activity.id]: e.target.value
                      }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleCommentSubmit(activity.id);
                        }
                      }}
                    />
                    <IconButton 
                      size="small" 
                      onClick={() => handleCommentSubmit(activity.id)}
                      disabled={!newComments[activity.id]?.trim()}
                    >
                      <Send />
                    </IconButton>
                  </Box>

                  {/* Mock Comments */}
                  <List dense disablePadding>
                    <ListItem disablePadding>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                          A
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>Alice Cooper:</strong> Great job! Keep it up! 💪
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            2 hours ago
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main' }}>
                          B
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>Bob Wilson:</strong> Inspiring! 🔥
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            1 hour ago
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export default ActivityFeed;